<template>
  <LcButton v-bind="{ size }" :loading="isStoring" @click="replayNumbers">
    {{ buttonLabel }}
  </LcButton>
</template>

<script lang="ts" setup>
import { LicensedTerritory } from '@lottocom/lottery-mapping'

import type {
  HighFrequency_GameId,
  LotteryId,
} from '#api/shared/@types/generated/backend/graphql-schema-types'

import { useProductStore } from '#product/app/stores/product'

type MyOrdersReplayButtonProps = {
  gameId?: HighFrequency_GameId
  lotteryId?: Maybe<LotteryId>
  playSlipOrderId?: Maybe<string>
  size?: 'medium' | 'small'
}

const {
  gameId,
  lotteryId,
  playSlipOrderId,
  size = 'medium',
} = defineProps<MyOrdersReplayButtonProps>()

const licensedTerritory = useLicensedTerritory({ raw: true })
const { t } = useI18n()
const { navigateToLocalized } = useRouterUtils()

const { mutate } = useReplayPlaySlipMutation()
const { mutate: mutateHighFrequency } = useHighFrequencyReplayPlaySlipMutation()
const { addToast, addToastError, addToastErrorDefault } = useToaster()

const apolloClient = useNuxtApp().$apollo.defaultClient

const refetchHighFrequencyQueries = async () => {
  await apolloClient.refetchQueries({
    include: [HighFrequencyOrdersDocument, HighFrequencyOrderDetailDocument],
  })
}

const isStoring = ref(false)

const buttonLabel = computed(() =>
  licensedTerritory === LicensedTerritory.US_NY
    ? t('myorders.replaybutton.ny.label')
    : t('myorders.replaybutton.label'),
)

const replayHighFrequency = async (hfgGameId: HighFrequency_GameId, orderId: string) => {
  try {
    const result = await mutateHighFrequency({
      input: {
        licensedTerritory,
        orderId,
      },
    })

    if (!result?.data) return

    if (isGraphqlSuccess(result?.data?.highFrequencyReplayPlaySlip)) {
      useProductStore().setProduct(
        result.data.highFrequencyReplayPlaySlip.id,
        hfgGameId,
        ProductType.HighFrequencyGame,
      )
      await navigateToLocalized({
        path: '/checkout',
        query: { p: hfgGameId.toLowerCase(), replay: 'true' },
      })
      return
    }

    const errors = result.data.highFrequencyReplayPlaySlip.errors

    if (hasGraphqlError(errors, 'HighFrequency_ReplayError_GameNotActiveError')) {
      addToast({
        message: t('myorders.replay.toast.error.game.notactive'),
        tracking: {
          action: 'checkout',
          topicKey: 'replay_high_frequency_game_not_active',
        },
        type: 'danger',
      })
      await refetchHighFrequencyQueries()
      return
    }

    addToastError({
      error: result.data.highFrequencyReplayPlaySlip.errors,
      prefix: 'myorders.replay.toast.error',
      tracking: {
        action: 'checkout',
        topicKey: 'replay_high_frequency_playslip_failed',
      },
    })
  } catch {
    addToastErrorDefault({ topicKey: 'replay_high_frequency_order' })
  }
}

type ReplayValidationError = {
  logMessage: string
  messageKey: string
  topicKey: string
}

const handleReplayValidationError = (
  id: LotteryId,
  orderId: string,
  { logMessage, messageKey, topicKey }: ReplayValidationError,
) => {
  addToast({
    message: t(messageKey, {
      lottery: t(`lottery.name.${id}`),
    }),
    tracking: {
      action: 'checkout',
      topicKey,
    },
    type: 'info',
  })
  useDatadog().log('warn', {
    appendPlayerInfo: true,
    component: 'MyOrdersReplayButton',
    message: logMessage,
    messageContext: {
      lotteryId: id,
      playSlipOrderId: orderId,
    },
  })
  usePlaySlip(id).reset()
  navigateToLocalized(`/${id.toLowerCase()}`)
}

const replayLottery = async (id: LotteryId, orderId: string) => {
  try {
    const result = await mutate({
      licensedTerritory,
      playSlipOrderId: orderId,
    })

    if (!result?.data) return
    useProductStore().setProduct(result.data.replayPlaySlip.id, id, ProductType.Lottery)

    await navigateToLocalized({
      path: '/checkout',
      query: { p: id.toLowerCase(), replay: 'true' },
    })
  } catch (error) {
    if (isSpecificGraphQLError(error, { matchMessage: 'validation.playslip.duplicate' })) {
      handleReplayValidationError(id, orderId, {
        logMessage: 'Duplicate play slip replay attempt',
        messageKey: 'validation.playslip.duplicate',
        topicKey: 'replay_playslip_validation_error',
      })
      return
    }

    if (isSpecificGraphQLError(error, { matchMessage: 'validation.field.numbers.outofbounds' })) {
      handleReplayValidationError(id, orderId, {
        logMessage: 'Out-of-bounds numbers replay attempt',
        messageKey: 'validation.field.numbers.outofbounds',
        topicKey: 'replay_playslip_numbers_outofbounds',
      })
      return
    }

    addToastError({
      error,
      prefix: 'myorders.replay.toast.error',
      tracking: {
        action: 'checkout',
        topicKey: 'replay_playslip_failed',
      },
    })
  }
}

const replayNumbers = async () => {
  if (!playSlipOrderId) return
  isStoring.value = true

  try {
    if (gameId) await replayHighFrequency(gameId, playSlipOrderId)
    else if (lotteryId) await replayLottery(lotteryId, playSlipOrderId)
  } finally {
    isStoring.value = false
  }
}
</script>
